/**
 * Sketch 测量工具 MTA Skill
 *
 * 功能：返回可直接传入 mcp_sketch_run_code 的测量脚本。
 *
 * v3.0 改进：所有命令均使用 mta-measure.sketchplugin 插件脚本，
 * 不再依赖旧版 sketch-tools.js。
 *
 * 命令映射:
 *   measure / auto → measure.js（完整测量）
 *   style         → style.js（快速样式检查）
 *   layout        → layout.js（布局分析）
 *   svg           → svg.js（SVG 图标导出）
 *
 * @module tools/sketchMeasure
 */

import * as fs from 'fs';
import * as os from 'os';
import * as path from 'path';
import { fileURLToPath } from 'url';

const __filename = fileURLToPath(import.meta.url);
const __dirname = path.dirname(__filename);

// ── 路径配置 ─────────────────────────────────────────────────────────────────

/**
 * 插件脚本路径
 * tsup 打包后 __dirname = dist/，所以向上一层是 mcp-server/
 */
const PLUGIN_SCRIPT_BASE = [
  path.resolve(__dirname, '../ui/sketch/mta-measure.sketchplugin/Contents/Sketch'),
  path.resolve(__dirname, '../../ui/sketch/mta-measure.sketchplugin/Contents/Sketch'),
];

/**
 * 插件包路径（用于检测 & 自动安装）
 */
const PLUGIN_BUNDLE_PATHS = [
  path.resolve(__dirname, '../ui/sketch/mta-measure.sketchplugin'),
  path.resolve(__dirname, '../../ui/sketch/mta-measure.sketchplugin'),
];

// macOS Sketch 插件目录
const SKETCH_PLUGINS_DIR = path.join(
  os.homedir(),
  'Library/Application Support/com.bohemiancoding.sketch3/Plugins'
);
const INSTALLED_PLUGIN_PATH = path.join(SKETCH_PLUGINS_DIR, 'mta-measure.sketchplugin');

// ── 常量 ─────────────────────────────────────────────────────────────────────

const VALID_CMDS = ['measure', 'compact', 'layout', 'style', 'svg', 'auto'] as const;
type SketchCmd = typeof VALID_CMDS[number];

// 命令 → 插件脚本文件映射（所有命令均使用插件脚本）
const PLUGIN_SCRIPT_FILES: Record<SketchCmd, string> = {
  measure: 'measure.js',
  compact: 'measure.js', // 与 measure 相同脚本，但注入 _FLUTTER_COMPACT=true
  auto: 'measure.js',
  style: 'style.js',
  layout: 'layout.js',
  svg: 'svg.js',
};

// ── 输出解读指南（AI 收到 JSON 后的关键上下文）────────────────────────────────
// 这些知识从 sketch-tools.js v3.6→v4.6 的 11 个版本迭代中提炼，
// 每条都对应一个曾导致还原失败的实际 BUG。
// AI 从不读取插件源码（temp file 直传 Sketch），因此必须在此处提供解读能力。

const OUTPUT_GUIDE: Record<string, string> = {
  measure: [
    '## 插件输出解读指南 (measure.js v3.2.0)',
    '',
    '### 设计原则',
    '- 插件 = 数据提取工具，不是代码生成器',
    '- 输出字段尽可能带 Flutter 代码提示（flutterColor/flutterBorder/flutterHint 等），可直接粘贴',
    '- 原始 Sketch 数据 + 坐标系转换参考，由你根据项目框架决定最终实现',
    '- **首轮还原先读 restorationContract**，再看 tree 级节点明细，避免被海量 JSON 或现有业务语义带偏',
    '',
    '### 页面级还原契约（v3.1.0 新增）',
    '- **restorationContract.pageTitle**: 页面标题真值，默认优先于现有路由标题和 i18n 文案',
    '- **restorationContract.blockOrder**: 顶层骨架顺序，代码应先按它还原页面层级，禁止自行重组为“常用/其他”等业务分区',
    '- **restorationContract.bodyCopyLedger/navCopyLedger**: 设计稿中测得的正文/底导文案，默认按原文还原',
    '- **restorationContract.topPrimaryEntry**: 若存在顶部主入口块，**禁止**迁移为底部固定 CTA',
    '- **restorationContract.bottomNavigation.present**: 若为 true 且 contract 未声明额外 CTA，禁止再额外增加底部操作条',
    '- **restorationContract.hardRules**: 生成代码前必须逐条检查；一旦违反，说明页面已发生结构漂移',
    '- **restorationContract.systemStatusBar.detected**: true 表示 Sketch 顶部时间/信号/WiFi/电池属于设备系统 chrome，Flutter 页面必须依赖 SafeArea/AppBar，禁止把这些元素画进业务 UI',
    '',
    '### 颜色字段（最常出错）',
    '- **colorSource**: "native" 表示通过 Cocoa API 获取的精确颜色（优先信任），"jsApi" 是 Sketch JS API 预处理值（可能不准）',
    '- **flutterColor**: Color(0xAARRGGBB) 格式，可直接粘贴到 Flutter 代码',
    '- **nativeColor/nativeTextColor**: 原生 API 颜色，当 JS API 的 fill.color 返回 #000000 或 rgba(0,0,0,1) 时，以原生值为准',
    '- Sketch 颜色格式 #RRGGBBAA（末两位alpha）→ Flutter Color(0xAARRGGBB)（首两位alpha），已自动转换',
    '',
    '### 渐变陷阱',
    '- 必须先检查 fill.fillType === "Gradient" 再读 gradient.stops，否则看到的是默认纯色',
    '- 渐变坐标系: Sketch 使用 0-1 归一化坐标，Flutter 使用 Alignment(-1 到 1)，转换公式: (sketch值 * 2) - 1',
    '- stops 中 colorSource="native" 的颜色才是真实渐变色，jsApi 返回的可能是 #000000ff',
    '',
    '### Group/Symbol 样式获取',
    '- Group 本身 style 通常为空，真实背景在 _background/_bg 子层 → 插件已提取到 visualStyle 字段',
    '- **symbolBackgroundTrap=true**: #4a60b2ff 是 Sketch Symbol 轮廓占位色，绝非真实背景！此时背景色应用 neumorphicBackground token',
    '- 阴影也在 _background 子层而非 Group 本身，插件已向下提取',
    '',
    '### 透明容器（v3.0.4 新增）',
    '- **isTransparentWrapper=true**: 此 Group 无任何填充和阴影，是纯透明包裹层',
    '  → Flutter 实现时**禁止**添加 Container 的 color/boxShadow/borderRadius',
    '  → 直接用 SizedBox 或 Padding 包裹子元素，无需任何装饰',
    '  → 根因: 无 visualStyle 输出 ≠ "未提取到装饰"，而是"该容器本就无装饰"',
    '- **flutterWrapperHint**: 透明容器的具体 Flutter 实现说明',
    '',
    '### 布局意图字段',
    '- **layoutIntent.sizing.width.type="FILL"**: 对应 Flutter double.infinity，禁止硬编码固定宽度',
    '- **nonCenterWarning**: 存在此字段时，禁止用 CrossAxisAlignment.center，必须用 Align(alignment: Alignment(x,y))',
    '- **flutterAlignY/flutterAlignX/flutterAlignment**: 已预算的 Flutter Alignment 值，直接使用',
    '- **visualSpacing**: Text 图层修正了行高空白后的真实视觉内边距，优先于 spacing 字段',
    '',
    '### siblingSpacing 准确性（v3.0.4 修复）',
    '- **siblingSpacing.left/right/above/below**: 当前图层与最近兄弟元素的视觉间距',
    '- v3.0.4 修复: getVisualBounds 现在对 Group 遍历所有子层，当子层 frame 超出 Group frame 时',
    '  自动扩展到子层 union 边界，解决"背景 Shape 溢出 Group"导致间距虚报的问题',
    '- 例: AUD 选择器 Group.frame.width=121 但内部背景 Shape.width=130（溢出9pt），',
    '  v3.0.4 前 siblingSpacing 报 41pt，修复后正确报 32pt',
    '',
    '### 文本字段',
    '- **strutHeight**: = frameHeight / fontSize，直接用作 StrutStyle(height: N)，消除 CJK 字体视觉偏下',
    '- **nativeRenderedWidth**: MSTextLayer 排版引擎实际渲染宽度（CJK 字体实际换行比 frame.width 窄）',
    '- **flutterTextStyle**: 插件现已直接输出完整 TextStyle（含 fontFamily/height/letterSpacing/color/fontWeight）',
    '- **flutterStrutStyle**: 固定高度或需要强校准行盒时，直接使用 StrutStyle(fontSize,height,leading:0,forceStrutHeight:true)',
    '- **parseFontWeight**: 保留字体真实可用字重；PingFang/Helvetica 只在超出平台上限时钳制，不再把 PingFangSC-Semibold 自动降成 w500',
    '',
    '### 符号字体图标（v3.0.3 新增）',
    '- **element.text.isSymbolFont=true**: 此文字图层使用 ZapfDingbats/Symbol/Webdings 等符号字体',
    '  → 字符在视觉上是图标，**禁止用 Flutter Text Widget 渲染**（跨平台字体不一致）',
    '  → **必须**导出为 SVG 后用 SvgPicture.asset() + colorFilter 渲染',
    '  → 颜色从 element.text.nativeTextColor.flutterColor 取（含透明度）',
    '- **element.symbolFontIconNote**: 已强制设置 element.isIcon=true，附带处理说明',
    '- **element.text.iconReplacementHint**: SVG 替代方案的具体操作步骤',
    '- 支持字体: ZapfDingbats/ZapfDingbatsITC、Symbol、Webdings、Wingdings(1/2/3)、FontAwesome、Material Icons 等',
    '',
    '### 图标字段',
    '- **isIcon=true**: 已通过 5 特征评分识别为图标，应导出 SVG 而非用 Icons.xxx',
    '- **iconSvgMarkup**: 插件现已直接返回从 Sketch 导出的真实 SVG 字符串，不再只猜测文件名',
    '- **iconSvgMeta.colorStrategy**: preserve=保持原色（多色/渐变/内嵌位图），tint=单色图标可用 colorFilter',
    '- **关键规则**: 当 colorStrategy=preserve，或 SVG 本身带 fill/fill-opacity/渐变时，**禁止**再套 ColorFilter，否则会出现颜色发灰、透明度被二次处理的问题',
    '- **flutterSvgCode**: 可直接粘贴的 SvgPicture.string(...) 代码；只有单色图标才会自动附带 ColorFilter',
    '- **iconContentBounds**: 图标内容实际边界（vs 容器尺寸），用于 SizedBox 约束',
    '- **iconExportPath/iconSvgFileName**: 若需要落盘资源，使用插件建议文件名保存到项目现有素材目录，目录名遵循项目约定而不是固定 assets/icons/',
    '- **页面局部修复规则**: 若问题只出在某个 chevron/flag/badge/icon，优先重新选中该图层执行 cmd=svg 或局部导出；不要直接复用其它页面的同名全局资产',
    '- **资源复用校验**: 旧资产在复用前，必须核对 viewBox、containerSize/contentSize、fill-opacity 和当前页面图层来源是否一致',
    '- 覆盖修正: 四边距≤2pt 或宽高≥父容器88% 的 ShapePath 是背景层，已自动排除',
    '',
    '### ShapePath 语义（v3.0.6 更新）',
    '- **shapeSig.guessedRole**: "icon"(用 SvgPicture) / "background"(用 BoxDecoration) / "divider"(用 Divider) / "icon-stroke"(复杂描边路径) / **"geometric-stroke"**(极简几何描边)',
    '- **shapeSig.guessedFlutter**: 推荐的 Flutter 写法提示',
    '- **shapeSig.strokeInfo**: 开放路径的描边信息（thickness、color、flutterColor）',
    '- **shapeSig.strokeSvgHint**: ⚠️ 开放路径专用警告 + SVG polyline 模板（仅 icon-stroke 角色）',
    '  → 存在此字段时，SVG 必须使用 fill="none" + stroke 而非 fill 实心',
    '  → 典型场景：下拉箭头 ∨、返回箭头 ‹ 等较复杂的描边图标',
    '- **shapeSig.flutterCustomPainterHint**: v3.0.6 新增，**geometric-stroke 专用**（≤4直线点，≤24×24）',
    '  → 存在此字段时，禁止用 Icons.xxx 或 SvgPicture；使用 CustomPainter + canvas.drawPath()',
    '  → 典型场景：radio button 勾选 ✓（3点 8×5）、小型箭头、小型折线',
    '  → 根因(2026-03): AI 看到“勾选路径”用 Icons.check；但 Icons.check 是填充形状，与描边设计不符，且底色与勾色被倒转',
    '- **shapeSig.cornerRadius**: v3.0.5 新增，从 ShapePath.points[].cornerRadius 提取的精确圆角值',
    '  → 对比 style.borderRadius (来自 layer.style.corners)，此字段从路径定义点提取，更可靠',
    '  → compact 模式已自动 fallback: 优先用 visualStyle/style 的 borderRadius，如果没有则用 shapeSig 的',
    '- **shapeSig.dashPattern**: v3.0.5 新增，虚线参数数组（如 [2,3] = 2pt画线 3pt间隔）',
    '  → compact 模式输出为 dash 字段',
    '',
    '### 虚线/分割线（v3.0.5 新增）',
    '- **dash=[n,m]**: 虚线参数，n=画线长度 m=间隔长度，直接用作 CustomPaint 的 dashPattern',
    '- **style.borderOptions**: 完整的边框选项 { dashPattern, lineEnd, lineJoin }',
    '  → lineEnd: "Butt"/"Round"/"Projecting" 对应 StrokeCap',
    '  → lineJoin: "Miter"/"Round"/"Bevel" 对应 StrokeJoin',
    '- 使用场景: coupon_card 中的虚线分割线，以前只提取 border.color+thickness 导致 AI 猜测 dashWidth',
    '',
    '### 边框颜色精度（v3.0.5 修复）',
    '- border 颜色现在与 fills/shadows 一样通过原生 Cocoa API 提取（之前只用 JS API 简化值）',
    '- **borderObj.nativeHex/nativeAlpha**: 原生 API 提取的精确边框颜色',
    '- **borderObj.flutterBorder**: 使用原生颜色生成的 Border.all(...)',
    '',
    '### 背景层 fallback（v3.0.5 修复）',
    '- resolveGroupVisualStyle 不再强制要求 _background 命名',
    '- 如果没有 _background 命名的子层，自动查找面积 >= 父容器 50% 的最大 Shape 子层作为背景',
    '- 解决: 设计稿中背景层命名为 Rectangle/bg/容器 等非标准名称时，cornerRadius/fills/shadows 丢失的问题',
    '',
    '### 子元素布局',
    '- **containerIntent.flutterDirective**: Sketch→Flutter 布局语义翻译，包含:',
    '  - mainAxisAlignment/crossAxisAlignment: 精确决策值',
    '  - spacersBetween[]: 哪些间隙应用 Spacer() 而非 SizedBox',
    '  - expandedChildren[]: 哪些子元素应用 Expanded',
    '  - containerPadding: 推算的 EdgeInsets',
    '  - rules[]: 每条决策的解释（调试用）',
    '',
    '### 阴影Token映射',
    '- **shadowToken**: 阴影数值已自动匹配的 AppShadows token（如 neumorphism/glassCard 等）',
    '- 存在 token 时优先使用 AppShadows.xxx 常量，避免硬编码 BoxShadow',
    '',
    '### Symbol 覆盖值',
    '- **symbolInfo.currentValues[]**: Symbol Instance 的实际运行值（文字/图片/子Symbol）',
    '- 禁止使用 expandedLayers 展开的默认内容，它们是 Master 值，不是当前 Instance 的值',
  ].join('\n'),

  style: [
    '## 插件输出解读指南 (style.js v2.0.0)',
    '',
    '输出结构: { directStyle, allStyles }',
    '- directStyle: 选中图层自身样式（fills/borders/shadows/innerShadows/blurs/corners）',
    '- allStyles: 递归提取所有含样式的后代图层（含路径 path[]）',
    '',
    '### 关键字段',
    '- fills[].flutterColor: 可直接粘贴的 Color(0xAARRGGBB)',
    '- borders[].position: "Inside"/"Outside"/"Center"，影响 Flutter Border 实现方式',
    '- blurs[].blurType + center: 包含模糊类型和中心点，区分高斯模糊 vs 动态模糊',
    '- corners: 来自 borderOptions 的精确圆角值',
    '',
    '### 使用场景',
    '当完整 measure 输出中样式数据不够详细时，用 style 命令对单个图层做深度检查',
  ].join('\n'),

  layout: [
    '## 插件输出解读指南 (layout.js v1.0.0)',
    '',
    '分析选中画板内所有子元素的排列方式和间距规律。',
    '输出 direction(ROW/COLUMN)、gaps[]（元素间距数组）、alignment（对齐方式）。',
    '',
    '### 关键字段',
    '- direction: 主轴方向，ROW=水平排列，COLUMN=垂直排列',
    '- uniformGap: 等间距值（如果所有间距一致）',
    '- alignment: 交叉轴对齐方式',
  ].join('\n'),

  svg: [
    '## 插件输出解读指南 (svg.js v1.0.0)',
    '',
    '将选中图层导出为 SVG 字符串。',
    '',
    '### 注意事项',
    '- 图标 = [圆形容器 + 图标图形] 时，选外层导出会含背景圆角矩形路径',
    '- 如果输出含 backgroundLayerWarnings，说明 SVG 中包含背景层，需要在 Sketch 中只选中图标图形部分重新导出',
    '- SVG 导出结果会同时返回 svgMarkup；单色图标可用 ColorFilter，多色/渐变图标必须保留原色',
  ].join('\n'),

  auto: '', // auto 等同 measure

  compact: [
    '## 插件输出解读指南 (measure.js compact 模式)',
    '',
    '输出体积约为完整 measure 的 3-8%，只保留 Flutter 代码生成所需字段。',
    '设计稿还原首轮**优先使用 compact**，因为它内联了 restorationContract，能先锁定页面骨架和文案真值。',
    '',
    '### 字段约定（缩短键名）',
    '- **n/t/f**: name / type / [x,y,w,h]',
    '- **bg**: Flutter 背景色 Color(0xAARRGGBB)',
    '- **gd**: LinearGradient(...) 完整代码',
    '- **sh**: BoxShadow 字符串数组，直接粘贴到 boxShadow: []',
    '- **bd**: Border.all(...) 完整代码',
    '- **token**: AppShadows.xxx token 名，优先使用代替硬编码 BoxShadow',
    '- **r**: borderRadius 数值',
    '- **rp**: [left,top,right,bottom] 相对父容器边距',
    '- **vf**: [x,y,w,h] 视觉边界；当背景层/阴影超出父 Group frame 时，容器尺寸和视觉底边距优先看 vf',
    '- **icon/svg/cs/xs**: 图标标识/真实 SvgPicture.string(...) 代码/containerSize/contentSize',
    '- **image/img/bitmapPath/bitmapScales/bitmapLookupCandidates/bitmapReuseMode**: 位图图层标识/Image.asset 代码/建议落盘路径/导出约定/优先搜索的手工图标文件名/是否 shared-canonical',
    '- **svgMode/svgReason/svgFile**: preserve|tint / 颜色策略原因 / 建议落盘文件名',
    '- **text/style/family/textHeight**: 文本内容/完整 TextStyle/字体族/行高比',
    '- **lh/strut**: strutHeight / 完整 StrutStyle(...)',
    '- **tag**: {bg,ph,pv,r,text} Tag容器信息',
    '- **layout**: {dir,gap,main,cross,pad} 布局意图',
    '- **ch**: 子元素数组（递归）',
    '- **designSurface**: {width,height,maxWidth,wideScreenRule} 设计画板尺寸参考；全屏 app 页面在宽屏应跟随宿主 Scaffold/SafeArea 和页面 padding，禁止把整页锁成 artboard 壳子',
    '- **selectionContext**: compact 若发现选中的是子图层，会自动提升到所属画板/顶层容器并记录 warning',
    '',
    '### 使用方式',
    '先读取 restorationContract.blockOrder/bodyCopyLedger/hardRules 锁定结构，再用 tree 中的 flutter* 字段填充样式和布局细节。',
    '',
    '### 还原后强制复查清单',
    '- 图标是否误用旧页面资产：同名箭头/图标默认不可信，先核对来源图层',
    '- SVG 是否误加 ColorFilter：保留原色的 SVG 不得二次染色',
    '- 图标是否用错尺寸：16x16 槽位不等于 16x16 图形，按 xs/cs 分离占位与渲染',
    '- Bitmap 是否误当 SVG：Sketch Image 图层必须优先在项目素材目录中按设计原名或 bitmapLookupCandidates 的文件名主体查找位图，命中后直接复用，不再伪造 SVG',
    '- 顶部 Help/Customer Service/Support Entry 若在 blockOrder 中是 Image，必须先按真实块名和 bitmapLookupCandidates 复用，不得用语义相近按钮替代',
    '- 全屏 app 页面在宽屏应跟随宿主 Scaffold/SafeArea/AppBar 和页面 padding，禁止把整页锁成 390 画板壳子',
    '- Sketch 顶部时间/信号/WiFi/电池属于设备系统 chrome，不属于页面业务 UI，禁止直接还原到 Flutter 页面',
    '- 若节点同时给出 f 与 vf，说明背景层/阴影超出逻辑 frame；容器尺寸和底部视觉间距优先使用 vf',
    '- 若 selectionContext.autoPromoted=true，说明之前选中了内层 glyph；后续局部导出图标时应改用 cmd=svg 选中具体图层',
    '- 页面骨架是否被业务组件改写：标题、顶部入口、搜索块和底导顺序必须回看 restorationContract',
  ].join('\n'),
};

// ── 接口 ─────────────────────────────────────────────────────────────────────

export interface SketchMeasureArgs {
  /** 执行模式: measure(完整测量)、style(样式快检)、layout(布局分析)、svg(导出图标)、auto(自动) */
  cmd?: string;
  /** SVG 导出路径，或 measure/compact/auto 的 JSON 结果落盘路径 */
  outputPath?: string;
}

// ── 工具函数 ─────────────────────────────────────────────────────────────────

/** 在候选路径列表中找到第一个存在的文件 */
function findFirst(paths: string[]): string | null {
  for (const p of paths) {
    if (fs.existsSync(p)) return p;
  }
  return null;
}

/** 查找插件 Sketch/ 目录 */
function findPluginScriptDir(): string | null {
  return findFirst(PLUGIN_SCRIPT_BASE);
}

/** 查找插件包目录 */
function findPluginBundle(): string | null {
  return findFirst(PLUGIN_BUNDLE_PATHS);
}

/**
 * 尝试将插件包通过符号链接安装到 Sketch 插件目录。
 * 已安装则跳过，安装失败不影响脚本返回。
 */
function tryAutoInstall(bundlePath: string): string {
  try {
    if (!fs.existsSync(SKETCH_PLUGINS_DIR)) {
      return 'Sketch 插件目录不存在，可能未安装 Sketch';
    }
    if (fs.existsSync(INSTALLED_PLUGIN_PATH)) {
      return 'already_installed';
    }
    fs.symlinkSync(bundlePath, INSTALLED_PLUGIN_PATH);
    return 'installed';
  } catch (e) {
    return `install_failed: ${(e as Error).message}`;
  }
}

// ── 主入口 ───────────────────────────────────────────────────────────────────

export async function sketchMeasure(args: SketchMeasureArgs): Promise<{
  content: Array<{ type: string; text: string }>;
}> {
  const cmd = (args.cmd || 'compact') as SketchCmd;

  if (!VALID_CMDS.includes(cmd)) {
    return {
      content: [{
        type: 'text',
        text: JSON.stringify({
          error: `无效的 cmd 参数: "${cmd}"`,
          validValues: VALID_CMDS,
          usage: 'mcp_mta_mta({ skill: "sketch_measure", params: { cmd: "compact" } })',
        }, null, 2),
      }],
    };
  }

  // 尝试自动安装插件（静默，失败不阻断主流程）
  const bundlePath = findPluginBundle();
  const installStatus = bundlePath ? tryAutoInstall(bundlePath) : 'bundle_not_found';

  // ── 加载插件脚本 ──────────────────────────────────────────────────────────

  const scriptDir = findPluginScriptDir();
  const scriptFile = PLUGIN_SCRIPT_FILES[cmd];
  const scriptPath = scriptDir ? path.join(scriptDir, scriptFile) : null;

  if (!scriptPath || !fs.existsSync(scriptPath)) {
    return {
      content: [{
        type: 'text',
        text: JSON.stringify({
          error: `插件脚本 ${scriptFile} 未找到`,
          searchedDirs: PLUGIN_SCRIPT_BASE,
          fix: '请确认 mta-measure.sketchplugin 插件包完整',
        }, null, 2),
      }],
    };
  }

  const scriptContent = fs.readFileSync(scriptPath, 'utf-8');
  const vm = scriptContent.match(/@version\s+([\d.]+)/);
  const scriptVersion = vm ? vm[1] : '1.0.0';
  const source = `plugin:${scriptFile}`;

  // 注入全局变量
  let fullScript = scriptContent;
  const measureOutputPath = cmd !== 'svg'
    ? (args.outputPath || path.join(os.tmpdir(), `mta_sketch_${cmd}_output.json`))
    : null;

  if (cmd === 'svg' && args.outputPath) {
    fullScript = `var _SVG_OUTPUT_PATH = '${args.outputPath.replace(/'/g, "\\'")}';\n` + scriptContent;
  } else if (cmd === 'compact') {
    // compact 模式：脚本内输出 Flutter 紧凑格式，体积约为完整格式的 3-8%
    if (measureOutputPath) {
      fullScript = `var _MEASURE_OUTPUT_PATH = '${measureOutputPath.replace(/'/g, "\\'")}';\n` + fullScript;
    }
    fullScript = `var _FLUTTER_COMPACT = true;\n` + fullScript;
  } else if (measureOutputPath) {
    fullScript = `var _MEASURE_OUTPUT_PATH = '${measureOutputPath.replace(/'/g, "\\'")}';\n` + fullScript;
  }

  // 写入临时文件（脚本不经过 AI 上下文，节省 ~30K tokens）
  const tempScriptPath = path.join(os.tmpdir(), 'mta_sketch_script.js');
  fs.writeFileSync(tempScriptPath, fullScript, 'utf-8');

  // 生成轻量加载器，通过 CocoaScript 的 NSString 从磁盘读取脚本文件并 eval 执行
  const loaderScript = [
    `var __p = '${tempScriptPath}';`,
    `var __c = NSString.stringWithContentsOfFile_encoding_error_(__p, 4, null);`,
    `if (__c) { eval(String(__c)); }`,
    `else { console.log(JSON.stringify({error: '脚本文件不存在: ' + __p, fix: '请重新调用 mcp_mta_mta skill=sketch_measure'})); }`,
  ].join('\n');

  return {
    content: [{
      type: 'text',
      text: JSON.stringify({
        status: 'ready',
        version: scriptVersion,
        cmd,
        source,
        scriptPath: tempScriptPath,
        scriptLength: fullScript.length,
        pluginInstall: installStatus,
        instruction: '将 loaderScript 传入 mcp_sketch_run_code({ code: loaderScript }) 执行。设计稿还原首轮优先使用 cmd=compact；只有当 compact contract 不足以解释局部布局或样式时，再补用 measure/style。若偏差集中在单个 icon/svg/bitmap，请先在项目现有素材目录中按设计稿图层名主体、bitmapLookupCandidates 和 bitmapReuseMode 查找真实位图或规范化副本，避免继续推断旧页面资产或伪造 SVG。文件名匹配重点看 xxx 主体，@4x 只是倍率后缀。顶部 Help/Customer Service/Support Entry 若 blockOrder 标记为 Image，必须先复用对应位图，未命中时应停止猜测。全屏 app 页面在宽屏应跟随宿主 Scaffold/SafeArea/AppBar 和页面 padding 适配；Sketch 顶部时间/信号/WiFi/电池属于设备系统 chrome，禁止直接还原到 Flutter 页面。compact 若检测到你选中的是内层 glyph，会自动提升到所属画板并通过 selectionContext 回报。请先在 Sketch 中选中目标画板/图层。',
        measureOutputPath,
        restoreChecklist: [
          '先按 restorationContract 锁定页面骨架，再处理局部样式',
          '单个图标偏差先查项目素材目录中设计稿同名、bitmapLookupCandidates 列出的同主体位图，再决定是否补导',
          'preserve 模式 SVG 禁止追加 ColorFilter',
          '16x16 槽位与 6x8/12x12 图形要分开处理',
          'Sketch Image 图层优先复用项目素材目录中按图层原名或同主体命中的位图，不要伪装成 SVG；只有旧位图流程才补 2.0x/3.0x',
          '顶部 Help/Customer Service/Support Entry 若测得为 Image，禁止用语义相近的其他按钮位图或 help glyph 替代',
          '全屏 app 页面在宽屏应跟随宿主 Scaffold/SafeArea/AppBar 和页面 padding，只有明确测得的局部浮层/卡片容器才做 clamp',
          'Sketch 顶部时间/信号/WiFi/电池属于设备系统 chrome，不属于页面业务 UI，禁止直接还原到 Flutter 页面',
          '若节点同时出现 f 与 vf，容器尺寸和底部视觉间距优先使用 vf，不要只抄逻辑 frame',
          '若 compact 返回 selectionContext.autoPromoted=true，说明之前误选中了子图层，不要把内层 glyph 当整页合同',
          '代码生成后必须调用 validate_restoration 校验页面骨架、资产来源和真实路由',
        ],
        postRestoreValidator: {
          skill: 'validate_restoration',
          required: true,
          checks: ['contract-validator', 'asset-source-validator', 'route-review-validator'],
          exampleArgs: {
            measurePath: measureOutputPath || '/path/to/sketch-compact-output.json',
            codeFilePath: '/path/to/generated_page.dart',
            routeFilePath: '/path/to/app_pages.dart',
            routeName: 'AppRoutes.xxx',
            projectPath: '/path/to/project',
          },
        },
        outputGuide: OUTPUT_GUIDE[cmd] || OUTPUT_GUIDE['measure'],
        loaderScript,
      }, null, 2),
    }],
  };
}
